import React, {useEffect, useState} from "react";


import {AppContext} from "../../HomePage";
import {UserService} from "../../service/UserService";
import {IndexTemplate, Loading} from "../../utils/InlineComponents";
import {formatCurrency} from "../../utils/Utils";
import {Panel} from "primereact/panel";
import {Bar, Line} from "react-chartjs-2";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";

// Two separate money views, on purpose:
//  * MRR / ARR (accounts/kpis/mrr): contracted subscription value per month.
//    Payment timing and payment method never move it.
//  * Cash (accounts/kpis/cash): money booked per charge month. Card rows are
//    bank confirmed; money-order (EFT) rows are written by the renewal jobs on
//    the billing day and are the invoiced amount, so they are drawn apart.
// The legacy accounts/kpis endpoint still feeds the user-count widgets.
const OverviewKPI = (props) =>{

    const {t, setError} = React.useContext(AppContext);


    const [loading, setLoading] = useState(false);
    const [kpis, setKpis] = useState([]);
    const [lastKpi, setLastKpi] = useState(null);
    const [mrrSeries, setMrrSeries] = useState([]);
    const [cashSeries, setCashSeries] = useState([]);
    const [forecast, setForecast] = useState(null);
    const [mrrDataSets, setMrrDataSets] = useState(null);
    const [arrDataSets, setArrDataSets] = useState(null);
    const [cashDataSets, setCashDataSets] = useState(null);
    const [userDataSets, setUserDataSets] = useState(null);

    const userService = new UserService();

    useEffect ( ()=>{
        getKpis()
    },[]);

    const lastMrr = mrrSeries.length > 0 ? mrrSeries[mrrSeries.length - 1] : null;
    const lastCash = cashSeries.length > 0 ? cashSeries[cashSeries.length - 1] : null;
    const previousCash = cashSeries.length > 1 ? cashSeries[cashSeries.length - 2] : null;

    // Solid line for the months that happened, dashed for the projection; the
    // two share the current month so the dashed part continues the solid one.
    const buildArrDataSets = (arrForecast) => {
        if (!arrForecast) return null;
        const actual = arrForecast.actual;
        const projected = arrForecast.forecast;
        return {
            labels: [...actual.map(row => row.date), ...projected.map(row => row.date)],
            datasets: [
                {
                    label: t('user.kpi.arr_actual'),
                    data: [...actual.map(row => row.arr), ...projected.map(() => null)],
                    fill: false,
                    borderColor: '#42A5F5'
                },
                {
                    label: t('user.kpi.arr_forecast'),
                    data: [...actual.map((row, index) => index === actual.length - 1 ? row.arr : null),
                        ...projected.map(row => row.arr)],
                    fill: false,
                    borderColor: '#7E57C2',
                    borderDash: [6, 4]
                }
            ]
        };
    };

    const getKpis = () => {
        setLoading(true);
        Promise.all([userService.getKpis(), userService.getKpisMrr(), userService.getKpisCash(),
            userService.getKpisForecast()]).then(([data, mrr, cash, arrForecast]) => {
            setKpis(data);
            setMrrSeries(mrr);
            setCashSeries(cash);
            setForecast(arrForecast);
            setArrDataSets(buildArrDataSets(arrForecast));
            setMrrDataSets(
                {
                    labels: mrr.map(row => row.date),
                    datasets: [
                        {
                            label: t('user.kpi.mrr'),
                            data: mrr.map(row => row.mrr),
                            fill: false,
                            borderColor: '#42A5F5'
                        },
                        {
                            label: t('user.kpi.basic_mrr'),
                            data: mrr.map(row => row.mrr_by_plan.basic),
                            fill: false,
                            borderColor: '#66BB6A'
                        },
                        {
                            label: t('user.kpi.professional_mrr'),
                            data: mrr.map(row => row.mrr_by_plan.professional),
                            fill: false,
                            borderColor: '#7E57C2'
                        },
                        {
                            label: t('user.kpi.premium_mrr'),
                            data: mrr.map(row => row.mrr_by_plan.premium),
                            fill: false,
                            borderColor: '#FFA726'
                        }
                    ]
                }
            )
            const cashDataSets = [
                {
                    label: t('user.kpi.cash_card'),
                    data: cash.map(row => row.by_method.card.amount_net),
                    backgroundColor: '#42A5F5'
                },
                {
                    label: t('user.kpi.cash_money_order'),
                    data: cash.map(row => row.by_method.money_order.amount_net),
                    backgroundColor: '#FFA726'
                },
                {
                    // Shopify app billing, net of Shopify's processing fee.
                    label: t('user.kpi.cash_shopify'),
                    data: cash.map(row => row.by_method.shopify?.amount_net ?? 0),
                    backgroundColor: '#66BB6A'
                }
            ];
            // Expenses stopped being entered in 2021; only draw them when the data has any.
            if (cash.some(row => row.expense > 0)) {
                cashDataSets.push({
                    label: t('user.kpi.total_expense'),
                    data: cash.map(row => -row.expense),
                    backgroundColor: '#f53438'
                });
            }
            setCashDataSets({labels: cash.map(row => row.date), datasets: cashDataSets});
            setUserDataSets(
                {
                    labels: data.map(kpi => kpi.date),
                    datasets: [
                        {
                            label: t('user.kpi.total_user'),
                            data: data.map(kpi => kpi.total_user_number),
                            fill: false,
                            borderColor: '#42A5F5'
                        },
                        {
                            label: t('user.kpi.total_local_user'),
                            data: data.map(kpi => kpi.total_local_user_number),
                            fill: false,
                            borderColor: '#66BB6A'
                        },
                        {
                            label: t('user.kpi.total_foreign_user'),
                            data: data.map(kpi => kpi.total_foreign_user_number),
                            fill: false,
                            borderColor: '#FFA726'
                        }
                    ]
                }

            )
            setLastKpi(data[data.length - 1])
            setLoading(false);
        }).catch(error =>{
            setError(error);
            setError(null);
            setLoading(false);
        });
    };

    const basicOptions = {
        plugins: {
            legend: {
                labels: {
                    color: '#495057'
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    color: '#495057'
                }
            },
            y: {
                ticks: {
                    color: '#495057'
                }
            }
        }
    };

    const stackedOptions = {
        ...basicOptions,
        plugins: {
            ...basicOptions.plugins,
            tooltip: {
                mode: 'index',
                intersect: false
            }
        },
        scales: {
            x: {...basicOptions.scales.x, stacked: true},
            y: {...basicOptions.scales.y, stacked: true}
        }
    };

    const gainedUsersTemplate = (rowData) => {
        let listItems = []
        rowData.foreign_gained_users.forEach(item => {
            listItems.push(<li><span style={{textAlign: 'center', marginBottom:"5px"}} className={"p-tag p-tag-success"}>{item}</span></li>)
        })
        rowData.local_gained_users.forEach(item => {
            listItems.push(<li><span style={{textAlign: 'center', marginBottom:"5px"}} className={"p-tag p-tag-info"}>{item}</span></li>)
        })
        return listItems;
    }

    const lostUsersTemplate = (rowData) => {
        let listItems = []
        rowData.foreign_lost_users.forEach(item => {
            listItems.push(<li><span style={{textAlign: 'center', marginBottom:"5px"}} className={"p-tag p-tag-danger"}>{item}</span></li>)
        })
        rowData.local_lost_users.forEach(item => {
            listItems.push(<li><span style={{textAlign: 'center', marginBottom:"5px"}} className={"p-tag p-tag-warning"}>{item}</span></li>)
        })
        return listItems;
    }

    const summaryCard = (title, detail, value, color) =>
        <div className="p-col-4">
            <div className="card summary">
                <button className="p-link" onClick={() => {}}>
                    <span className="title">{title}</span>
                </button>
                <span className="detail">{detail}</span>
                <span className={"count " + color}>{value}</span>
            </div>
        </div>;

    const planMrrDetail = (plan) =>
        t('user.kpi.plan_mrr_detail', {count: lastMrr?.customers_by_plan?.[plan] ?? 0});

    return <React.Fragment>

        {loading ?
            <div className="p-col-12" style={{textAlign:'center'}}>
                <Loading/>
            </div> :
            <React.Fragment>
                <div className="p-grid p-fluid dashboard card">
                    {summaryCard(t('user.kpi.mrr'),
                        t('user.kpi.mrr_detail', {customers: lastMrr?.customers ?? 0, unpaid: lastMrr?.unpaid_mrr ?? 0}),
                        formatCurrency(lastMrr?.mrr, 'USD'), 'blue')}
                    {summaryCard(t('user.kpi.arr'), t('user.kpi.arr_detail'),
                        formatCurrency(lastMrr?.arr, 'USD'), 'green')}
                    {summaryCard(t('user.kpi.net_new_mrr'),
                        t('user.kpi.net_new_mrr_detail', {
                            new: lastMrr?.new_mrr ?? 0, expansion: lastMrr?.expansion_mrr ?? 0,
                            contraction: lastMrr?.contraction_mrr ?? 0, churn: lastMrr?.churned_mrr ?? 0}),
                        formatCurrency(lastMrr?.net_new_mrr, 'USD'), (lastMrr?.net_new_mrr ?? 0) < 0 ? 'red' : 'green')}

                    {summaryCard(t('user.kpi.year_end_arr'),
                        t('user.kpi.year_end_arr_detail', {year: forecast?.year, growth: forecast?.monthly_mrr_growth ?? 0}),
                        formatCurrency(forecast?.year_end_arr, 'USD'),
                        (forecast?.year_end_arr ?? 0) < (forecast?.current_arr ?? 0) ? 'red' : 'green')}
                    {summaryCard(t('user.kpi.expected_year_revenue', {year: forecast?.year}),
                        t('user.kpi.expected_year_revenue_detail', {
                            ytd: forecast?.ytd_revenue ?? 0, remaining: forecast?.forecast_remaining_revenue ?? 0,
                            previous_year: forecast?.previous_year_revenue ?? 0}),
                        formatCurrency(forecast?.expected_year_revenue, 'USD'), 'blue')}
                    <div className="p-col-4"/>

                    {summaryCard(t('user.kpi.cash_in'),
                        t('user.kpi.cash_in_detail', {
                            card: lastCash?.by_method?.card?.amount_net ?? 0,
                            money_order: lastCash?.by_method?.money_order?.amount_net ?? 0,
                            shopify: lastCash?.by_method?.shopify?.amount_net ?? 0}),
                        formatCurrency(lastCash?.cash_in_net, 'USD'), 'blue')}
                    {summaryCard(t('user.kpi.cash_in_previous'),
                        t('user.kpi.cash_in_detail', {
                            card: previousCash?.by_method?.card?.amount_net ?? 0,
                            money_order: previousCash?.by_method?.money_order?.amount_net ?? 0,
                            shopify: previousCash?.by_method?.shopify?.amount_net ?? 0}),
                        formatCurrency(previousCash?.cash_in_net, 'USD'), 'green')}
                    {summaryCard(t('user.kpi.payments'),
                        t('user.kpi.payments_detail', {payers: lastCash?.payers ?? 0}),
                        lastCash?.payments ?? 0, 'yellow')}

                    {summaryCard(t('user.kpi.basic_mrr'), planMrrDetail('basic'),
                        formatCurrency(lastMrr?.mrr_by_plan?.basic, 'USD'), 'green')}
                    {summaryCard(t('user.kpi.professional_mrr'), planMrrDetail('professional'),
                        formatCurrency(lastMrr?.mrr_by_plan?.professional, 'USD'), 'blue')}
                    {summaryCard(t('user.kpi.premium_mrr'), planMrrDetail('premium'),
                        formatCurrency(lastMrr?.mrr_by_plan?.premium, 'USD'), 'yellow')}

                    {summaryCard(t('user.kpi.total_user'), t('user.kpi.total_user_detail'), lastKpi?.total_user_number, 'blue')}
                    {summaryCard(t('user.kpi.total_foreign_user'), t('user.kpi.total_foreign_user_detail'), lastKpi?.total_foreign_user_number, 'yellow')}
                    {summaryCard(t('user.kpi.total_local_user'), t('user.kpi.total_local_user_detail'), lastKpi?.total_local_user_number, 'green')}

                    <div className="p-col-12 p-lg-6 contacts">
                        <Panel header={t('user.kpi.mrr_chart')}>
                            <div className="card">
                                {mrrDataSets &&
                                <Line data={mrrDataSets} options={basicOptions} />}
                            </div>
                        </Panel>
                    </div>

                    <div className="p-col-12 p-lg-6 contacts">
                        <Panel header={t('user.kpi.arr_chart', {year: forecast?.year})}>
                            <div className="card">
                                {arrDataSets &&
                                <Line data={arrDataSets} options={basicOptions} />}
                            </div>
                        </Panel>
                    </div>

                    <div className="p-col-12 p-lg-6 contacts">
                        <Panel header={t('user.kpi.cash_chart')}>
                            <div className="card">
                                {cashDataSets &&
                                <Bar data={cashDataSets} options={stackedOptions} />}
                            </div>
                        </Panel>
                    </div>

                    <div className="p-col-12 p-lg-6 contacts">
                        <Panel header={t('user.kpi.user_numbers')}>
                            <div className="card">
                                {userDataSets &&
                                <Line data={userDataSets} options={basicOptions} />}
                            </div>
                        </Panel>
                    </div>

                    <div className="p-col-12 p-lg-6 contacts">
                        <DataTable value={mrrSeries} sortField="date" sortOrder={-1} rows={12} paginator>
                            <Column field="date" header={t('user.kpi.date')} style={{width:'7em'}}/>
                            <Column field="mrr" header={t('user.kpi.mrr')} body={row => formatCurrency(row.mrr, 'USD')}/>
                            <Column field="customers" header={t('user.kpi.customers')}/>
                            <Column field="new_mrr" header={t('user.kpi.new_mrr')} body={row => formatCurrency(row.new_mrr, 'USD')}/>
                            <Column field="churned_mrr" header={t('user.kpi.churned_mrr')} body={row => formatCurrency(row.churned_mrr, 'USD')}/>
                            <Column field="unpaid_mrr" header={t('user.kpi.unpaid_mrr')} body={row => formatCurrency(row.unpaid_mrr, 'USD')}/>
                        </DataTable>
                    </div>

                    <div className="p-col-12 p-lg-8 contacts">
                        <DataTable value={kpis} sortField="date" sortOrder={-1}>
                            <Column field="date" header={t('user.kpi.date')} style={{width:'10em'}}/>
                            <Column body={gainedUsersTemplate} header={t('user.kpi.gained_users')} />
                            <Column body={lostUsersTemplate} header={t('user.kpi.lost_users')} />
                        </DataTable>
                    </div>

                    <div className="p-col-12 p-lg-1 contacts">
                    </div>

                    <div className="p-col-12 p-lg-3 contacts">
                        <DataTable value={lastKpi?.user_number_of_countries}>
                            <Column header={t('table_headers.index')} style={{width: '5em'}} body={IndexTemplate}/>
                            <Column field="country" header={t('suggestion.country')} style={{width:'10em'}}/>
                            <Column field="count" header={t('product.count')} style={{width:'10em'}}/>
                        </DataTable>
                    </div>

                </div>

            </React.Fragment>
        }

    </React.Fragment>;
};
export default OverviewKPI;
